import Layout from "../../components/layout/Layout";
import React from "react";

export default function TermsAndConditions(props) {
  const {
    companyName = "Predixio Technologies Ltd.",
    productName = "Predixio",
    domain = "predixio.com",
    contactEmail = "legal@predixio.com",
    governingLaw = "England & Wales",
    arbitrationLocale = "London, United Kingdom",
    consumerEUContact = "eu-legal@predixio.com",
    dmcaEmail = "dmca@predixio.com",
    lastUpdated = new Date().toISOString().slice(0, 10),
  } = props;

  const ui = {
    page: { background: "#f7f7f9", minHeight: "100vh", padding: "40px 16px" },
    container: { maxWidth: 1400, margin: "0 auto", display: "grid", gap: 24, boxSizing: "border-box" },
    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,.08)",
      borderRadius: 14,
      boxShadow: "0 8px 24px rgba(0,0,0,.05)",
      overflow: "hidden",
    },
    sidebar: { padding: 16 },
    main: { padding: 28 },
    title: { margin: 0, fontSize: 28, fontWeight: 800, color: "#0f172a" },
    subtitle: { marginTop: 6, fontSize: 14, color: "#6b7280" },
    sectionTitle: { fontSize: 18, fontWeight: 800, margin: "28px 0 10px", color: "#111827" },
    p: { margin: "10px 0", color: "#334155", lineHeight: 1.7 },
    li: { margin: "6px 0", color: "#334155", lineHeight: 1.6 },
    badge: {
      display: "inline-block",
      fontSize: 12,
      color: "#2563eb",
      background: "rgba(37,99,235,.10)",
      border: "1px solid rgba(37,99,235,.25)",
      padding: "4px 10px",
      borderRadius: 999,
      marginRight: 8,
    },
    hr: { border: "none", borderTop: "1px solid rgba(0,0,0,.06)", margin: "20px 0" },
    tocTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 },
    tocLink: { display: "block", fontSize: 13, color: "#334155", padding: "6px 8px", borderRadius: 8, textDecoration: "none" },
    tocLinkActive: { background: "rgba(37,99,235,.08)", color: "#1d4ed8", border: "1px solid rgba(37,99,235,.25)" },
    callout: {
      background: "linear-gradient(180deg, #fff, #fafafa)",
      border: "1px dashed rgba(0,0,0,.15)",
      borderRadius: 12,
      padding: 12,
      color: "#374151",
      fontSize: 13,
    },
    dl: { display: "grid", gridTemplateColumns: "220px 1fr", gap: 8 },
  };

  const sections = [
    { id: "intro", label: "1. Introduction" },
    { id: "definitions", label: "2. Definitions" },
    { id: "eligibility", label: "3. Eligibility & Accounts" },
    { id: "subscriptions", label: "4. Subscriptions, Billing & Refunds" },
    { id: "predictions", label: "5. Predictions & Responsible Use" },
    { id: "data-sources", label: "6. Data Sources & Third-Party Services" },
    { id: "api-fairuse", label: "7. API Access, Rate Limits & Fair Use" },
    { id: "user-content", label: "8. User Content & Acceptable Use" },
    { id: "ip-rights", label: "9. Intellectual Property" },
    { id: "privacy", label: "10. Privacy & Cookies" },
    { id: "availability", label: "11. Service Availability & Changes" },
    { id: "termination", label: "12. Suspension & Termination" },
    { id: "disclaimer", label: "13. Disclaimers" },
    { id: "liability", label: "14. Limitation of Liability" },
    { id: "indemnity", label: "15. Indemnification" },
    { id: "law-disputes", label: "16. Governing Law & Dispute Resolution" },
    { id: "consumers", label: "17. Consumer Rights (EU/UK)" },
    { id: "dmca", label: "18. Copyright/DMCA" },
    { id: "export", label: "19. Export & Sanctions" },
    { id: "children", label: "20. Children" },
    { id: "changes", label: "21. Changes to these Terms" },
    { id: "contact", label: "22. Contact" },
  ];

  const [active, setActive] = React.useState(sections[0].id);
  React.useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 1] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const A = ({ href, children }) => (
    <a href={href} style={{ color: "#1d4ed8", textDecoration: "none" }}>
      {children}
    </a>
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Terms of Service">
      <style>{`
        .tos-page, .tos-container, .tos-card, .tos-main { box-sizing: border-box; max-width: 100%; overflow-x: clip; }

        /* Desktop: sidebar | main */
        .tos-container {
          display: grid;
          grid-template-columns: 280px 1fr;
          grid-template-areas: "sidebar main";
          gap: 24px;
        }
        .tos-sidebar { grid-area: sidebar; position: sticky; top: 24px; align-self: start; }
        .tos-main    { grid-area: main; }
        .tos-toc a { white-space: normal; }

        /* Make sure long content never overflows horizontally */
        .tos-main img, .tos-main table, .tos-main code { max-width: 100%; }
        .tos-table-wrap { overflow-x: auto; }

        /* Tablet */
        @media (max-width: 1024px) {
          .tos-container { grid-template-columns: 240px 1fr; gap: 20px; }
        }

        /* Mobile: stack one after the other (sidebar THEN content) */
        @media (max-width: 768px) {
          .tos-container {
            grid-template-columns: 1fr;
            grid-template-areas:
              "sidebar"
              "main";
            gap: 16px;
          }
          .tos-sidebar { position: static; top: auto; width: 100%; }
          .tos-title { font-size: 22px !important; }
        }
      `}</style>
      <div className="tos-page" style={ui.page}>
        <div className="tos-container" style={ui.container}>
          <aside className="tos-card tos-sidebar" style={{ ...ui.card, ...ui.sidebar }}>
            <div style={ui.tocTitle}>Terms & Conditions</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              Last updated: <span style={{ fontWeight: 700 }}>{lastUpdated}</span>
            </div>
            <hr style={ui.hr} />
            <nav className="tos-toc">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  style={{ ...ui.tocLink, ...(active === s.id ? ui.tocLinkActive : null) }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <main className="tos-card tos-main" style={{ ...ui.card, ...ui.main }}>
            <div style={{ marginBottom: 8 }}>
              <span style={ui.badge}>{productName}</span>
              <span style={ui.badge}>Legal</span>
            </div>
            <h1 className="tos-title" style={ui.title}>Terms & Conditions</h1>
            <p style={ui.subtitle}>
              These Terms & Conditions (“Terms”) form a legally binding agreement between you and {companyName} (“we”, “us”, “our”)
              governing your access to and use of {productName} at <strong>{domain}</strong> and associated APIs, applications, and services
              (collectively, the “Service”).
            </p>
            <div style={ui.callout}>
              <strong>Not legal advice.</strong> This page is provided for product use; please obtain independent legal advice to ensure
              these Terms suit your jurisdiction and business needs.
            </div>

            {/* 1. Introduction */}
            <section id="intro">
              <h2 style={ui.sectionTitle}>1. Introduction</h2>
              <p style={ui.p}>
                By creating an account, accessing, or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
                You also agree to any policies referenced here (e.g., Privacy Policy, Acceptable Use).
              </p>
              <p style={ui.p}>
                We may update these Terms as described in Section 21. Your continued use after an update constitutes acceptance.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 2. Definitions */}
            <section id="definitions">
              <h2 style={ui.sectionTitle}>2. Definitions</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}><strong>Account</strong>: A registered user profile that enables access to the Service.</li>
                <li style={ui.li}><strong>Content</strong>: Data, text, graphics, statistics, odds, predictions, or other materials.</li>
                <li style={ui.li}><strong>Paid Plan</strong>: Any subscription or one-time purchase offering enhanced features.</li>
                <li style={ui.li}><strong>Prediction</strong>: Model-generated probability or forecast (e.g., W/D/L, goals, cards).</li>
                <li style={ui.li}><strong>Third-Party Services</strong>: External providers (e.g., data feeds, payment processors).</li>
                <li style={ui.li}><strong>API</strong>: Programmatic access endpoints we provide to retrieve or submit data.</li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 3. Eligibility & Accounts */}
            <section id="eligibility">
              <h2 style={ui.sectionTitle}>3. Eligibility & Accounts</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service.</li>
                <li style={ui.li}>You are responsible for your Account credentials and all activity under your Account.</li>
                <li style={ui.li}>Provide accurate information; keep it updated. We may suspend accounts for false or unlawful use.</li>
                <li style={ui.li}>You may not share, resell, or transfer your Account without our written consent.</li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 4. Subscriptions, Billing & Refunds */}
            <section id="subscriptions">
              <h2 style={ui.sectionTitle}>4. Subscriptions, Billing & Refunds</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>
                  <strong>Plans.</strong> We may offer Free and Paid Plans. Plan features, limits, and prices are described on <A href={`https://${domain}/pricing`}>our pricing page</A>.
                </li>
                <li style={ui.li}>
                  <strong>Billing.</strong> Paid Plans auto-renew unless canceled. You authorize us (or our processor) to charge your payment method.
                </li>
                <li style={ui.li}>
                  <strong>Taxes.</strong> Fees are exclusive of taxes; you are responsible for applicable taxes.
                </li>
                <li style={ui.li}>
                  <strong>Refunds.</strong> Except where required by law or explicitly stated, fees are non-refundable. For EU/UK consumers, see Section 17.
                </li>
                <li style={ui.li}>
                  <strong>Upgrades/Downgrades.</strong> Changes take effect immediately or at the next billing cycle as indicated during checkout.
                </li>
                <li style={ui.li}>
                  <strong>Delinquency.</strong> We may suspend or terminate for non-payment after reasonable notice.
                </li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 5. Predictions & Responsible Use */}
            <section id="predictions">
              <h2 style={ui.sectionTitle}>5. Predictions & Responsible Use</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>
                  {productName} provides <strong>informational predictions and probabilities</strong> only. No prediction guarantees an outcome.
                  Past performance does not guarantee future results.
                </li>
                <li style={ui.li}>
                  We do <strong>not</strong> provide financial, investment, or gambling advice. If you choose to place bets, you do so at your own risk.
                </li>
                <li style={ui.li}>
                  <strong>Responsible betting.</strong> Wager only what you can afford to lose. If you need support, visit{" "}
                  <A href="https://www.begambleaware.org">BeGambleAware</A> or your local responsible-gaming resource.
                </li>
                <li style={ui.li}>
                  You acknowledge that model outputs may be incomplete, delayed, inaccurate, or unavailable due to data quality or latency.
                </li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 6. Data Sources & Third-Party Services */}
            <section id="data-sources">
              <h2 style={ui.sectionTitle}>6. Data Sources & Third-Party Services</h2>
              <p style={ui.p}>
                The Service may integrate third-party data and APIs (e.g., fixtures, standings, transfers). Your use may be subject to those
                providers’ terms. We are not responsible for third-party content or availability.
              </p>
              <p style={ui.p}>
                We may change data providers at any time. Third-party outages or changes can affect the Service. Reasonable caching and rate-limit
                policies may be applied.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 7. API Access, Rate Limits & Fair Use */}
            <section id="api-fairuse">
              <h2 style={ui.sectionTitle}>7. API Access, Rate Limits & Fair Use</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>
                  If we provide API keys, you must keep them confidential. You are responsible for requests made with your credentials.
                </li>
                <li style={ui.li}>
                  We may impose <strong>rate limits</strong>, quotas, or concurrent request caps. Exceeding limits may result in throttling (e.g., HTTP 429),
                  temporary blocks, or suspension.
                </li>
                <li style={ui.li}>
                  Caching and backoff mechanisms are recommended to reduce load and avoid rate-limit breaches.
                </li>
                <li style={ui.li}>
                  You may not scrape, mirror, or bulk-export data beyond the scope of your plan or applicable provider terms.
                </li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 8. User Content & Acceptable Use */}
            <section id="user-content">
              <h2 style={ui.sectionTitle}>8. User Content & Acceptable Use</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>
                  You are responsible for content you upload or transmit. Do not submit illegal, infringing, defamatory, or harmful content.
                </li>
                <li style={ui.li}>
                  Prohibited behaviors include: interfering with the Service, scanning or probing, reverse engineering, circumventing security,
                  or reselling access without authorization.
                </li>
                <li style={ui.li}>
                  We may remove content or suspend accounts that violate these Terms or law.
                </li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 9. Intellectual Property */}
            <section id="ip-rights">
              <h2 style={ui.sectionTitle}>9. Intellectual Property</h2>
              <p style={ui.p}>
                The Service, including models, software, UI, and aggregated statistics, are owned by {companyName} or its licensors and are
                protected by IP laws. You receive a limited, revocable, non-exclusive license to access and use the Service as permitted by
                these Terms and your plan.
              </p>
              <p style={ui.p}>
                You may not copy, modify, distribute, or create derivative works from the Service except as expressly allowed. All trademarks,
                logos, and service marks are the property of their respective owners.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 10. Privacy & Cookies */}
            <section id="privacy">
              <h2 style={ui.sectionTitle}>10. Privacy & Cookies</h2>
              <p style={ui.p}>
                Your use is subject to our <A href={`https://${domain}/privacy`}>Privacy Policy</A> and <A href={`https://${domain}/cookies`}>Cookie Policy</A>.
                By using the Service, you consent to our data practices as described therein.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 11. Service Availability & Changes */}
            <section id="availability">
              <h2 style={ui.sectionTitle}>11. Service Availability & Changes</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}>We aim for high availability but do not guarantee uninterrupted access.</li>
                <li style={ui.li}>We may modify, suspend, or discontinue features with or without notice.</li>
                <li style={ui.li}>Planned maintenance or third-party outages may cause delays or temporary unavailability.</li>
              </ul>
            </section>
            <hr style={ui.hr} />

            {/* 12. Suspension & Termination */}
            <section id="termination">
              <h2 style={ui.sectionTitle}>12. Suspension & Termination</h2>
              <p style={ui.p}>
                We may suspend or terminate your access for violations, suspected fraud, non-payment, or risk. Upon termination, your right to
                use the Service ceases immediately, but clauses that by nature should survive (e.g., IP, disclaimers, liability, dispute
                resolution) will survive.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 13. Disclaimers */}
            <section id="disclaimer">
              <h2 style={ui.sectionTitle}>13. Disclaimers</h2>
              <p style={ui.p}>
                THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT PREDICTIONS OR CONTENT ARE
                ACCURATE, RELIABLE, OR ERROR-FREE.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 14. Limitation of Liability */}
            <section id="liability">
              <h2 style={ui.sectionTitle}>14. Limitation of Liability</h2>
              <p style={ui.p}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName} AND ITS AFFILIATES WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL, ARISING FROM OR
                RELATED TO YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY.
              </p>
              <p style={ui.p}>
                OUR AGGREGATE LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO US IN THE 12 MONTHS
                PRECEDING THE CLAIM OR (B) USD $100.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 15. Indemnification */}
            <section id="indemnity">
              <h2 style={ui.sectionTitle}>15. Indemnification</h2>
              <p style={ui.p}>
                You agree to indemnify and hold harmless {companyName}, its affiliates, and personnel from any claims, damages, liabilities,
                and expenses (including reasonable legal fees) arising out of your use of the Service, violation of these Terms, or infringement
                of any third-party rights.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 16. Governing Law & Dispute Resolution */}
            <section id="law-disputes">
              <h2 style={ui.sectionTitle}>16. Governing Law & Dispute Resolution</h2>
              <p style={ui.p}>
                These Terms are governed by the laws of <strong>{governingLaw}</strong>, without regard to its conflict of laws rules.
              </p>
              <p style={ui.p}>
                <strong>Arbitration.</strong> Any dispute arising out of or relating to these Terms or the Service will be finally resolved by
                confidential, binding arbitration in <strong>{arbitrationLocale}</strong>, before a single arbitrator, in English, under the rules of
                a recognized arbitration body. You and we waive the right to a jury trial and to participate in a class action, to the extent
                permitted by law.
              </p>
              <p style={ui.p}>
                Nothing herein limits either party’s right to seek injunctive relief for IP or security breaches in any competent court.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 17. Consumer Rights */}
            <section id="consumers">
              <h2 style={ui.sectionTitle}>17. Consumer Rights (EU/UK)</h2>
              <p style={ui.p}>
                If you are a consumer residing in the EU or UK, you may have additional statutory rights. Where applicable, you may withdraw
                from a distance contract within 14 days of purchase; however, you acknowledge that digital services or content delivered during
                the withdrawal period may affect your right to a refund. Contact: <A href={`mailto:${consumerEUContact}`}>{consumerEUContact}</A>.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 18. DMCA */}
            <section id="dmca">
              <h2 style={ui.sectionTitle}>18. Copyright/DMCA</h2>
              <p style={ui.p}>
                If you believe material available through the Service infringes your copyright, please email:{" "}
                <A href={`mailto:${dmcaEmail}`}>{dmcaEmail}</A> with a compliant notice including (1) your contact info, (2) work claimed to be
                infringed, (3) material location/URL, (4) a statement of good-faith belief, (5) a statement under penalty of perjury, and (6) your
                signature. We may remove content and/or terminate repeat infringers as appropriate.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 19. Export & Sanctions */}
            <section id="export">
              <h2 style={ui.sectionTitle}>19. Export & Sanctions</h2>
              <p style={ui.p}>
                You represent that you are not located in, under the control of, or a national or resident of any country or entity subject to
                sanctions or export restrictions. You agree to comply with applicable export control and sanctions laws.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 20. Children */}
            <section id="children">
              <h2 style={ui.sectionTitle}>20. Children</h2>
              <p style={ui.p}>
                The Service is not directed to children under 13 (or the applicable age in your jurisdiction). Do not use the Service if you
                are under the minimum age.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 21. Changes */}
            <section id="changes">
              <h2 style={ui.sectionTitle}>21. Changes to these Terms</h2>
              <p style={ui.p}>
                We may modify these Terms from time to time. Material changes will be notified via the Service or email. The “Last updated”
                date reflects the latest revision. Continued use after changes constitutes acceptance.
              </p>
            </section>
            <hr style={ui.hr} />

            {/* 22. Contact */}
            <section id="contact">
              <h2 style={ui.sectionTitle}>22. Contact</h2>
              <p style={ui.p}>
                For questions or legal notices, contact us at: <A href={`mailto:${contactEmail}`}>{contactEmail}</A>.{" "}
                Postal correspondence: Legal Department, {companyName}.
              </p>
              <div style={{ ...ui.callout, marginTop: 12 }}>
                <div style={ui.dl}>
                  <div>Service</div><div>{productName} ({domain})</div>
                  <div>Company</div><div>{companyName}</div>
                  <div>Governing law</div><div>{governingLaw}</div>
                  <div>Arbitration</div><div>{arbitrationLocale}</div>
                  <div>Last updated</div><div>{lastUpdated}</div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </Layout>
  );
}
